/**
 * AvatarCropperModal — moderno editor de recorte de avatar.
 *
 * Sprint UX Workspace v1.34.
 *
 * Muestra un modal a pantalla completa con:
 *   • Vista previa del área circular exacta que será el avatar.
 *   • Zoom (rueda del mouse o slider) desde 1x hasta 3x.
 *   • Pan (drag) para centrar la parte que se quiere mostrar.
 *   • Botones "Cancelar" y "Guardar" que exportan un JPEG cuadrado
 *     de 512x512 listo para /api/auth/avatar.
 *
 * Utiliza `react-easy-crop` que ya está en `package.json`.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { X, ZoomIn, ZoomOut, Save } from 'lucide-react';

interface AvatarCropperModalProps {
  fileDataUrl: string;              // objectURL o base64 del archivo original
  onCancel: () => void;
  onSave: (croppedJpegDataUrl: string) => Promise<void> | void;
  saving?: boolean;
}

/**
 * Draw the cropped area onto a canvas and export as a JPEG data URL.
 * Output is a 512×512 square (perfectly circular avatar consumers can
 * apply their own `border-radius: 50%` mask on top).
 */
async function getCroppedJpeg(
  imageSrc: string,
  cropAreaPx: Area,
  outSize = 512,
  quality = 0.9,
): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = outSize;
  canvas.height = outSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    img,
    cropAreaPx.x, cropAreaPx.y, cropAreaPx.width, cropAreaPx.height,
    0, 0, outSize, outSize,
  );
  return canvas.toDataURL('image/jpeg', quality);
}

export const AvatarCropperModal: React.FC<AvatarCropperModalProps> = ({
  fileDataUrl, onCancel, onSave, saving = false,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelCrop, setPixelCrop] = useState<Area | null>(null);

  // Sprint v1.35 · responsive cropSize so the circle never overflows on
  // narrow viewports. Uses the smaller of (viewport-2×32px) and 420px.
  const [cropSize, setCropSize] = useState({ width: 420, height: 420 });
  useEffect(() => {
    const compute = () => {
      const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
      const side = Math.min(420, Math.max(220, w - 96));
      setCropSize({ width: side, height: side });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const onCropComplete = useCallback((_cropped: Area, croppedPx: Area) => {
    setPixelCrop(croppedPx);
  }, []);

  const handleSave = async () => {
    if (!pixelCrop) return;
    try {
      const dataUrl = await getCroppedJpeg(fileDataUrl, pixelCrop, 512, 0.9);
      await onSave(dataUrl);
    } catch (e) {
      // The parent decides what to do with the error via its own catch.
      console.error('avatar cropper: onSave threw', e);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4"
      style={{ background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(8px)' }}
      data-testid="avatar-cropper-modal"
    >
      <div
        className="w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl flex flex-col"
        style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))' }}
      >
        {/* Header */}
        <div
          className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: '1px solid hsl(var(--border))' }}
        >
          <div>
            <div className="text-sm font-semibold" style={{ color: 'hsl(var(--text))' }}>
              Ajusta tu foto de perfil
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: 'hsl(var(--text-muted))' }}>
              Mueve, haz zoom y centra. Lo que ves en el círculo será tu avatar.
            </div>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-full grid place-items-center transition-colors"
            style={{ background: 'hsl(var(--bg-elev))', color: 'hsl(var(--text-muted))' }}
            aria-label="Cerrar editor"
            data-testid="avatar-cropper-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Cropper canvas — Sprint v1.35 · círculo mucho más grande +
            zoom-out permitido hasta 0.5x para que el usuario pueda
            encajar imágenes verticales u horizontales de golpe. */}
        <div
          className="relative w-full"
          style={{ height: 'min(72vh, 620px)', background: '#000' }}
        >
          <Cropper
            image={fileDataUrl}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            minZoom={0.5}
            maxZoom={4}
            restrictPosition={false}
            cropSize={cropSize}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { background: '#000' },
              cropAreaStyle: {
                border: '2px solid hsl(var(--accent))',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
              },
            }}
          />
        </div>

        {/* Zoom controls */}
        <div className="px-5 py-4 flex items-center gap-3" style={{ background: 'hsl(var(--bg-elev))' }}>
          <button
            onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.2).toFixed(2)))}
            className="w-9 h-9 rounded-full grid place-items-center shrink-0 transition-colors"
            style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
            aria-label="Alejar"
            data-testid="avatar-cropper-zoom-out"
          >
            <ZoomOut size={14} />
          </button>
          <input
            type="range"
            min={0.5}
            max={4}
            step={0.02}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-1 accent-current cursor-pointer"
            style={{ accentColor: 'hsl(var(--accent))' }}
            aria-label="Zoom"
            data-testid="avatar-cropper-zoom-slider"
          />
          <button
            onClick={() => setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)))}
            className="w-9 h-9 rounded-full grid place-items-center shrink-0 transition-colors"
            style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
            aria-label="Acercar"
            data-testid="avatar-cropper-zoom-in"
          >
            <ZoomIn size={14} />
          </button>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 flex items-center justify-end gap-2" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 rounded-full text-xs font-medium transition-colors disabled:opacity-50"
            style={{ background: 'transparent', color: 'hsl(var(--text-muted))', border: '1px solid hsl(var(--border))' }}
            data-testid="avatar-cropper-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !pixelCrop}
            className="px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-60"
            style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
            data-testid="avatar-cropper-save"
          >
            <Save size={13} />
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarCropperModal;
